// @spec INC-STR-001
'use client';

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ClipboardList } from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { IncidentForm } from '@/features/incidents/components/IncidentForm';
import type { Incident } from '@/features/incidents/types';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/incidents/new')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.addNewIncident'),
  }),
  component: NewIncidentPage,
});

/**
 * New Incident page - guided form for reporting incidents.
 *
 * Provides a multi-section form for:
 * - Basic information (title, description, dates)
 * - Classification (category, type, severity, priority, tags)
 * - Attachments (optional files)
 * - Linked assets (optional)
 */
function NewIncidentPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const handleSuccess = (_incident: Incident) => {
    // Navigate to the incidents list for now
    // In the future, this would navigate to the incident detail page
    void navigate({ to: '/incidents' });
  };

  const handleCancel = () => {
    void navigate({ to: '/incidents' });
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Header */}
        <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('common:routeErrors.addNewIncident')}</h1>
            <p className="mt-1 text-muted-foreground">
              Document a security, operational, compliance, or safety incident for response and
              tracking.
            </p>
          </div>
          <Link to="/surveys/campaigns/new">
            <Button variant="outline" className="w-full sm:w-auto">
              <ClipboardList className="mr-2 h-4 w-4" />
              Send Survey
            </Button>
          </Link>
        </header>

        {/* Form */}
        <IncidentForm onSuccess={handleSuccess} onCancel={handleCancel} />
      </div>
    </div>
  );
}
